/* eslint-disable @typescript-eslint/indent */
import { GenderType } from '@t/api/audience/enums/gender.enum';
import { OutOfStateDonorType } from '@t/api/audience/enums/out-of-state-donor.enum';

import { defaultCategories } from 'types/api/audience/defaults';
import { AudienceConstants, DataPointSearchCategory, DonorType, SearchType } from 'types/api/audience/enums';
import { IFormCategory, IFormDataPoint } from 'types/api/audience/form';
import { s } from 'utilities/strings';

type SharedValues = Pick<
  IFormDataPoint,
  'category' | 'formName' | 'numericHigh' | 'numericLow' | 'slideInterval' | 'source' | 'textValues'
>;

export const defaultDataPoints = defaultCategories.map((category) => {
  switch (category) {
    default: {
      const sharedValues: SharedValues = {
        category,
        formName: '',
        numericHigh: null,
        numericLow: null,
        slideInterval: null,
        source: category,
        textValues: null,
      };
      let dataPoint: IFormDataPoint[] = [];

      if (category === AudienceConstants.RFM) {
        dataPoint = [
          {
            ...sharedValues,
            group: s('Site.Pages.AudienceBuilder.Recency'),
            label: s(['Site.Date', 'Site.Specific']),
            name: 'h_date_range',
            searchCategory: DataPointSearchCategory.Basic,
            summaryLabel: s('Site.Pages.AudienceBuilder.Recency'),
            supportedOperators: ['='],
            type: 'date-range',
            values: ['', ''],
          },
          {
            ...sharedValues,
            group: s('Site.Pages.AudienceBuilder.Recency'),
            label: s(['Site.Date', 'Site.Range']),
            menuDataPoint: 'h_recency_type',
            name: 'h_recency',
            searchCategory: DataPointSearchCategory.Basic,
            summaryLabel: s('Site.Pages.AudienceBuilder.Recency'),
            supportedOperators: ['='],
            type: 'recency',
            values: {
              Day: [0, 10, 20, 30],
              Month: [0, 3, 6, 9, 12, 18, 24, 36],
              Year: [0, 1, 2, 3],
              [s('Site.OpenEnded')]: ['', ''],
            },
          },
          {
            ...sharedValues,
            group: s('Site.Pages.AudienceBuilder.Frequency'),
            label: s('Site.Interval'),
            name: 'h_frequency',
            searchCategory: DataPointSearchCategory.Basic | DataPointSearchCategory.Conditions,
            type: 'frequency',
            values: [],
          },
          {
            ...sharedValues,
            children: ['h_dollar_low', 'h_dollar_high'],
            group: s('Site.Dollar'),
            label: s('Site.Amount'),
            menuDataPoint: 'h_dollar_type',
            name: 'h_dollar',
            searchCategory: DataPointSearchCategory.Basic,
            type: 'donation',
            values: [],
          },
          {
            ...sharedValues,
            label: `${s(['Site.Date', 'Site.Range'])} (${s(['Site.Low'])})`,
            name: 'h_recency_low',
            searchCategory: DataPointSearchCategory.Conditions,
            supportedOperators: ['='],
            type: 'recency-low',
            values: [0, 3, 6, 9, 12, 18, 24, 36],
          },
          {
            ...sharedValues,
            label: `${s(['Site.Date', 'Site.Range'])} (${s(['Site.High'])})`,
            name: 'h_recency_high',
            searchCategory: DataPointSearchCategory.Conditions,
            supportedOperators: ['='],
            type: 'recency-high',
            values: [0, 3, 6, 9, 12, 18, 24, 36],
          },
          {
            ...sharedValues,
            label: `${s(['Site.Date', 'Site.Specific'])} (${s(['Site.Start'])})`,
            name: 'h_date_range_low',
            searchCategory: DataPointSearchCategory.Conditions,
            summaryLabel: `${s(['Site.Date', 'Site.Specific'])} (${s(['Site.Start'])})`,
            supportedOperators: ['='],
            type: 'date-range-low',
            values: [],
          },
          {
            ...sharedValues,
            label: `${s(['Site.Date', 'Site.Specific'])} (${s(['Site.End'])})`,
            name: 'h_date_range_high',
            searchCategory: DataPointSearchCategory.Conditions,
            summaryLabel: `${s(['Site.Date', 'Site.Specific'])} (${s(['Site.End'])})`,
            supportedOperators: ['='],
            type: 'date-range-high',
            values: [],
          },
          {
            ...sharedValues,
            label: s(['Site.Dollar', 'Site.Low']),
            name: 'h_dollar_low',
            searchCategory: DataPointSearchCategory.Conditions,
            summaryLabel: s('Site.Low'),
            supportedOperators: ['>', '>='],
            type: 'donation-low',
            values: [5, 10, 25, 50, 100],
          },
          {
            ...sharedValues,
            label: s(['Site.Dollar', 'Site.Cap']),
            name: 'h_dollar_high',
            searchCategory: DataPointSearchCategory.Conditions,
            summaryLabel: s('Site.Cap'),
            supportedOperators: ['<', '<='],
            type: 'donation-high',
            values: [s('Site.Pages.AudienceBuilder.Create.NoCap'), 49, 99, 249, 999],
          },
          {
            ...sharedValues,
            label: s('Site.Pages.AudienceBuilder.DollarType'),
            name: 'h_dollar_type',
            searchCategory: DataPointSearchCategory.Menu,
            supportedOperators: ['='],
            type: 'dollar-type',
            values: [
              s('Site.Pages.AudienceBuilder.Create.HPC'),
              s('Site.Pages.AudienceBuilder.Create.MRC'),
              s('Site.Amount'),
            ],
          },
          {
            ...sharedValues,
            label: s(['Site.Pages.AudienceBuilder.Recency', 'Site.Interval']),
            name: 'h_recency_type',
            searchCategory: DataPointSearchCategory.Menu,
            supportedOperators: ['='],
            type: 'recency-type',
            values: [s('Site.Month'), s('Site.Day'), s('Site.Year'), s('Site.OpenEnded')],
          },
        ];
      } else {
        dataPoint = [
          {
            ...sharedValues,
            formName: `${SearchType.Attributes}.includeOutOfStateDonor`,
            group: s('Site.Attribute', { plural: true }),
            label: s(['Site.Pages.AudienceBuilder.Create.OutOfState', 'Site.Donors']),
            name: 'includeOutOfStateDonor',
            searchCategory: DataPointSearchCategory.Attributes,
            supportedOperators: ['='],
            type: 'out-of-state-donor',
            values: [OutOfStateDonorType.Instate, OutOfStateDonorType.OutOfState, OutOfStateDonorType.Both],
          },
          {
            ...sharedValues,
            formName: `${SearchType.Attributes}.includeNonDonor`,
            group: s('Site.Attribute', { plural: true }),
            label: s(['Site.Donor', 'Site.Type']),
            name: 'includeNonDonor',
            searchCategory: DataPointSearchCategory.Miscellaneous,
            supportedOperators: ['='],
            type: 'donor-type',
            values: [DonorType.Donors, DonorType.Both, DonorType.NonDonors],
          },
          {
            ...sharedValues,
            formName: `${SearchType.Attributes}.excludeBusiness`,
            group: s('Site.Attribute', { plural: true }),
            label: s(['Site.Exclude', 'Site.Business'], { plural: true }),
            name: 'excludeBusiness',
            searchCategory: DataPointSearchCategory.Miscellaneous,
            supportedOperators: ['='],
            type: 'toggle',
            values: ['0', '1'],
          },
          {
            ...sharedValues,
            formName: `${SearchType.Gender}`,
            group: s('Site.Attribute', { plural: true }),
            label: s('Site.Gender'),
            name: 'gender',
            searchCategory: DataPointSearchCategory.Miscellaneous,
            supportedOperators: ['='],
            type: 'gender',
            values: [
              GenderType.ExcludeBoth,
              GenderType.ExcludeFemale,
              GenderType.ExcludeMale,
              GenderType.IncludeMale,
              GenderType.IncludeFemale,
              GenderType.IncludeBoth,
            ],
          },
        ];
      }

      return {
        category,
        dataPoint,
        source: s('Site.Standard'),
      } as IFormCategory;
    }
  }
});
